import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  Card,
  Modal,
} from "@mui/material";
import React, { useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteRoleMutation,
  useListPeopleQuery,
  useLazyGetUserImageQuery,
} from "../apis/rolesAPI";
import { getOrganisation } from "../slices/organisationSlice";
import { selector } from "../store";
import { theme } from "../theme";
import {
  DataGrid,
  GridRowsProp,
  GridColDef,
  GridCellParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef,
  GridRowId,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
  GridPrintExportOptions,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import colors from "../colors";
import { ViewContainer } from "./components/viewContainer";
import { useTranslation } from "react-i18next";
import { isSettingEnabled } from "../utils/settingUtils";
import { useListSettingsQuery } from "../apis/settingsAPI";
import { InviteModal } from "./components/modals/inviteModal";
import {
  useInviteToCourseMutation,
  useLazyGetCourseLinkQuery,
  useListCoursesQuery,
} from "../apis/coursesAPI";

const ConvertedDate = (params: GridCellParams) => {
  return (
    <Typography style={{ fontSize: 14 }}>
      {params.row.dateOfBirth
        ? dayjs(params.row.dateOfBirth).format("DD.MM.YYYY")
        : ""}
    </Typography>
  );
};

const LocalizedStatus = (params: GridCellParams) => {
  const { t } = useTranslation();
  return (
    <Typography style={{ fontSize: 14 }}>{t(params.row.userStatus)}</Typography>
  );
};

export const People = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [invite] = useInviteToCourseMutation();

  const [filter, setFilter] = useState<
    "all" | "visitor" | "contractor" | "employee"
  >("all");
  const org: any = selector((state) => getOrganisation(state));

  const {
    data: people,
    isFetching: loading,
    isLoading: peopleLoading,
  } = useListPeopleQuery(org!, {
    skip: !org,
  });
  const { data: settings, isFetching: loadingSettings } = useListSettingsQuery({
    organisationId: org?.organisationId || "",
  });
  const { data: courses, isLoading: coursesLoading } = useListCoursesQuery(
    org?.organisationId,
    {
      skip: !org,
    }
  );

  const [getImage, userImageUrl] = useLazyGetUserImageQuery();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteErrorModal, setShowDeleteErrorModal] = useState(false);

  const [deleteRole] = useDeleteRoleMutation();

  const ImageCell = (params: GridCellParams<any, string>) => {
    return params.value ? (
      <Button
        style={{ fontSize: 14 }}
        onClick={async () => {
          const image = await getImage({
            organisationId: org?.organisationId,
            id: params.value,
          });
          setShowImageModal(true);
        }}
      >
        {params.value ? t("imageContent") : ""}
      </Button>
    ) : (
      <></>
    );
  };

  const onDelete = useCallback(
    async (id: GridRowId) => {
      const idString = id.toString();
      const role = people?.find((p) => p.id === idString)?.role;

      if (org) {
        try {
          if (role === "user" && !loading) {
            await deleteRole({
              organisationId: org.organisationId,
              userId: idString,
            }).unwrap();
          } else {
            console.log("Can't delete a manager");
            setShowDeleteErrorModal(true);
          }
        } catch (err) {
          console.log("error at onDelete");
          setError(true);
        }
      }
    },
    [deleteRole, org, people, loading]
  );

  const onDeleteSelected = useCallback(
    async (ids: GridRowId[]) => {
      ids.forEach(async (id) => {
        try {
          console.log("delete " + id);
          await onDelete(id);
        } catch {
          console.log("error at onDeleteSelected");
          setError(true);
        }
      });
    },
    [onDelete]
  );

  const sendInvitations = useCallback(
    async (userIds: string[], courses: string[]) => {
      await Promise.all(
        courses.map(async (course) => {
          await invite({
            organisationId: org?.organisationId || "",
            courseId: course,
            userIds,
          }).unwrap();
        })
      );
    },
    [org, invite]
  );

  const filteredPeople = useMemo(() => {
    if (filter === "all") return people;
    else return people?.filter((p) => p.userStatus === filter);
  }, [people, filter]);

  // Row and column -data for the table.
  const data: GridRowsProp =
    filteredPeople?.map((p) => {
      return {
        ...p,
        edit: t("edit"),
        courses: t("coursesTitle"),
      };
    }) || [];
  const columns: GridColDef[] = useMemo(() => {
    let result = [
      { field: "firstName", headerName: t("firstName"), width: 120 },
      { field: "lastName", headerName: t("lastName"), width: 120 },
      { field: "email", headerName: t("email"), width: 180 },
      { field: "company", headerName: t("company"), width: 120 },
      { field: "phoneNumber", headerName: t("phoneNumber"), width: 120 },
      { field: "taxNumber", headerName: t("taxNumber"), width: 80 },
      {
        field: "userStatus",
        headerName: t("userType"),
        width: 100,
        renderCell: LocalizedStatus,
      },
      {
        field: "dateOfBirth",
        headerName: t("dateOfBirth"),
        width: 100,
        renderCell: ConvertedDate,
      },
      { field: "hotWorkCard", headerName: t("hotWorkCard"), width: 80 },
      {
        field: "occupationalSafetyCard",
        headerName: t("occupationalSafetyCard"),
        width: 80,
      },
      // ImageCell for cells with image content.
      {
        field: "firstAidCardImage",
        headerName: t("firstAidCardImage"),
        width: 80,
        renderCell: (params: GridCellParams<any, string>) => ImageCell(params),
      },
      {
        field: "occupationalSafetyCardImage",
        headerName: t("occupationalSafetyCardImage"),
        width: 80,
        renderCell: (params: GridCellParams<any, string>) => ImageCell(params),
      },
      {
        field: "hotWorkCardImage",
        headerName: t("hotWorkCardImage"),
        width: 80,
        renderCell: (params: GridCellParams<any, string>) => ImageCell(params),
      },
      { field: "shoeSize", headerName: t("shoeSize"), width: 80 },
      { field: "jacketSize", headerName: t("jacketSize"), width: 80 },
      {
        field: "dietaryRestrictions",
        headerName: t("dietaryRestrictions"),
        width: 80,
      },
      { field: "visitationDate", headerName: t("visitationDate"), width: 80 },
    ];

    if (!settings || !isSettingEnabled(settings, "companyVisible")) {
      result = result.filter((r) => r.field !== "company");
    }
    if (!settings || !isSettingEnabled(settings, "taxnumberVisible")) {
      result = result.filter((r) => r.field !== "taxNumber");
    }
    if (!settings || !isSettingEnabled(settings, "phoneVisible")) {
      result = result.filter((r) => r.field !== "phoneNumber");
    }
    if (!settings || !isSettingEnabled(settings, "dateOfBirthVisible")) {
      result = result.filter((r) => r.field !== "dateOfBirth");
    }
    if (!settings || !isSettingEnabled(settings, "hotWorkCardVisible")) {
      result = result.filter((r) => r.field !== "hotWorkCard");
    }
    if (
      !settings ||
      !isSettingEnabled(settings, "occupationalSafetyCardVisible")
    ) {
      result = result.filter((r) => r.field !== "occupationalSafetyCard");
    }
    if (!settings || !isSettingEnabled(settings, "hotWorkCardImageVisible")) {
      result = result.filter((r) => r.field !== "hotWorkCardImage");
    }
    if (
      !settings ||
      !isSettingEnabled(settings, "occupationalSafetyCardImageVisible")
    ) {
      result = result.filter((r) => r.field !== "occupationalSafetyCardImage");
    }
    if (!settings || !isSettingEnabled(settings, "firstAidCardImageVisible")) {
      result = result.filter((r) => r.field !== "firstAidCardImage");
    }
    if (!settings || !isSettingEnabled(settings, "shoeSizeVisible")) {
      result = result.filter((r) => r.field !== "shoeSize");
    }
    if (!settings || !isSettingEnabled(settings, "jacketSizeVisible")) {
      result = result.filter((r) => r.field !== "jacketSize");
    }
    if (
      !settings ||
      !isSettingEnabled(settings, "dietaryRestrictionsVisible")
    ) {
      result = result.filter((r) => r.field !== "dietaryRestrictions");
    }
    if (!settings || !isSettingEnabled(settings, "visitationDateVisible")) {
      result = result.filter((r) => r.field !== "visitationDate");
    }

    return result;
  }, [t, settings]);

  // Checkboxes as the last column.
  const columnsWithCheckbox = React.useMemo(
    () => [
      ...columns,
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        width: 60,
      },
    ],
    [columns]
  );

  // Create a custom toolbar for grid view.
  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        {/* Print-button does not work, hidden for now */}
        <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  }

  return (
    <ViewContainer loading={peopleLoading || loadingSettings}>
      <Box position={"relative"}>
        <Snackbar
          open={error}
          autoHideDuration={3000}
          onClose={() => setError(false)}
        >
          <Alert
            onClose={() => setError(false)}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {t("errorOccured")}
          </Alert>
        </Snackbar>
        <Typography variant="h4" style={{ paddingBottom: theme.spacing(4) }}>
          {t("peopleTitle")}
        </Typography>
        <Button
          onClick={() => setFilter("all")}
          variant={filter === "all" ? "contained" : "outlined"}
          sx={{ margin: theme.spacing(0, 1) }}
        >
          {t("all")}
        </Button>
        <Button
          onClick={() => setFilter("employee")}
          variant={filter === "employee" ? "contained" : "outlined"}
          sx={{ margin: theme.spacing(0, 1) }}
        >
          {t("employees")}
        </Button>
        <Button
          onClick={() => setFilter("contractor")}
          variant={filter === "contractor" ? "contained" : "outlined"}
          sx={{ margin: theme.spacing(0, 1) }}
        >
          {t("contractors")}
        </Button>
        <Button
          onClick={() => setFilter("visitor")}
          variant={filter === "visitor" ? "contained" : "outlined"}
          sx={{ margin: theme.spacing(0, 1) }}
        >
          {t("visitors")}
        </Button>
        {filteredPeople && (
          <Box sx={{ width: "100%" }}>
            <DataGrid
              sx={{
                width: "calc(100vw - 250px)", // check mobile view
                minWidth: 300,
                borderStyle: "none",
                height: "75%",
                ".MuiDataGrid-cell": {
                  borderColor: "#555",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
              checkboxSelection
              rows={data}
              onRowSelectionModelChange={(newRowSelectionModel) => {
                // Selecting a checkbox saves the id of every selected row on the list to rowSelectionModel-state
                setRowSelectionModel(newRowSelectionModel);
              }}
              columns={columnsWithCheckbox}
              onCellClick={(param) => {
                // If the user clicks on image-cells, use the image event instead of navigate-event.
                // Disable also navigation on checkboxes.
                if (
                  !(
                    param.field.includes("Image") ||
                    param.field.includes("_check_")
                  )
                ) {
                  navigate("/people/person", {
                    state: {
                      person: param.row,
                    },
                  });
                }
              }}
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              pageSizeOptions={[10, 25, 100]}
              slots={{ toolbar: CustomToolbar }}
              slotProps={{}}
              localeText={{
                toolbarQuickFilterPlaceholder: t("search"),
                toolbarDensity: t("density"),
                toolbarDensityLabel: t("density"),
                toolbarDensityCompact: t("small"),
                toolbarDensityStandard: t("medium"),
                toolbarDensityComfortable: t("large"),
                toolbarColumns: t("columns"),
                toolbarFilters: t("filter"),
                toolbarExport: t("export"),
                toolbarColumnsLabel: t("columns"),
                toolbarFiltersLabel: t("filter"),
              }}
            />
          </Box>
        )}

        {/* Add-button */}
        <Button
          variant="text"
          style={{ marginTop: theme.spacing(2) }}
          onClick={() => navigate("/people/add")}
        >
          {t("addPerson")}
        </Button>

        {/* Delete-button */}
        {rowSelectionModel.length > 0 ? (
          <Button
            variant="text"
            style={{ marginTop: theme.spacing(2) }}
            onClick={() => {
              setShowDeleteModal(true);
            }}
          >
            {t("deletePerson")}
          </Button>
        ) : (
          <></>
        )}

        {/* Invite-button */}
        {rowSelectionModel.length > 0 ? (
          <Button
            variant="text"
            style={{
              marginLeft: theme.spacing(4),
              marginTop: theme.spacing(2),
            }}
            onClick={() => {
              setShowInviteModal(true);
            }}
          >
            {t("inviteToCourse")}
          </Button>
        ) : (
          <></>
        )}

        {/* MODALS */}
        {/* Invite modal */}
        <InviteModal
          users={rowSelectionModel}
          courses={courses || []}
          sendInvite={(users, courses) => {
            console.log(users, courses);
            sendInvitations(users as string[], courses as string[]);
          }}
          isVisible={showInviteModal}
          setVisible={(val) => setShowInviteModal(val)}
        />

        {/* Confirmation modal for deleting people */}
        <Modal
          open={showDeleteModal}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: theme.spacing(3),
              background: colors.backgroundSecondary,
            }}
          >
            <Typography>{t("deleteConfirmation")}</Typography>

            {rowSelectionModel.map((email) => {
              const person = people?.find((p) => p.email == email);
              return (
                <Typography>
                  {person?.firstName} {person?.lastName}
                </Typography>
              );
            })}

            <Button
              variant="text"
              style={{ paddingTop: theme.spacing(2) }}
              onClick={() => {
                console.log(rowSelectionModel);
                onDeleteSelected(rowSelectionModel);
                setShowDeleteModal(false);
              }}
            >
              {t("ok")}
            </Button>
            <Button
              variant="text"
              style={{ paddingTop: theme.spacing(2) }}
              onClick={() => {
                console.log(rowSelectionModel);
                setShowDeleteModal(false);
              }}
            >
              {t("cancel")}
            </Button>
          </Card>
        </Modal>

        {/* Error modal for trying to delete a manager */}
        <Modal
          open={showDeleteErrorModal}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: theme.spacing(3),
              background: colors.backgroundSecondary,
            }}
          >
            <Typography>{t("cannotRemoveManager")}</Typography>
            <Button
              variant="text"
              style={{ paddingTop: theme.spacing(2) }}
              onClick={() => {
                setShowDeleteErrorModal(false);
              }}
            >
              {t("ok")}
            </Button>
          </Card>
        </Modal>
        {/* Modal for showing images of the workers cards */}
        <Modal
          open={showImageModal}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: theme.spacing(3),
              background: colors.backgroundSecondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
            }}
          >
            <img
              style={{
                maxWidth: "70%",
                maxHeight: "calc(70%)",
                objectFit: "scale-down",
                aspectRatio: "contain",
                overflow: "scroll",
              }}
              src={userImageUrl?.data || undefined}
            />
            <Button
              variant="text"
              style={{
                flex: 1,
                paddingTop: theme.spacing(2),
                alignSelf: "center",
              }}
              onClick={() => {
                setShowImageModal(false);
              }}
            >
              {t("ok")}
            </Button>
          </Card>
        </Modal>
      </Box>
    </ViewContainer>
  );
};
